import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '../lib/api.js';
import { useCreateClient } from '../lib/clients.js';
import { useLead } from '../lib/leads.js';

/* ============================================================================
   /clients/new — a won lead becoming a customer.
   ----------------------------------------------------------------------------
   Reached with `?leadId=...` from a lead, or empty for someone who arrived by
   word of mouth.

   The lead's details are PREFILLED, not copied. A lead's `name` is whoever
   filled in the form; a client's `ownerName` is who we invoice, and those are
   often different people at the same shop. Its email is optional and a
   client's is not, because that is where the invoice goes. Prefilling puts
   both differences in front of the person converting, at the moment they can
   still fix them.
   ========================================================================== */

interface Fields {
  business: string;
  ownerName: string;
  phone: string;
  email: string;
  gstin: string;
}

const EMPTY: Fields = { business: '', ownerName: '', phone: '', email: '', gstin: '' };

export function NewClientPage() {
  const [params] = useSearchParams();
  const leadId = params.get('leadId') ?? '';
  const navigate = useNavigate();

  const lead = useLead(leadId);
  const create = useCreateClient();

  const [fields, setFields] = useState<Fields>(EMPTY);
  /* Whether the form has been prefilled once. Without it, a background
     refetch of the lead would overwrite whatever the user had typed. */
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (!leadId || prefilled || !lead.data) return;
    const source = lead.data.lead;
    setFields({
      business: source.business,
      ownerName: source.name ?? '',
      phone: source.phone,
      email: source.email ?? '',
      gstin: '',
    });
    setPrefilled(true);
  }, [leadId, lead.data, prefilled]);

  function set<K extends keyof Fields>(key: K, value: string) {
    setFields((current) => ({ ...current, [key]: value }));
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate(
      {
        ...(leadId ? { leadId } : {}),
        business: fields.business.trim(),
        ownerName: fields.ownerName.trim(),
        phone: fields.phone.trim(),
        email: fields.email.trim(),
        ...(fields.gstin.trim() ? { gstin: fields.gstin.trim() } : {}),
      },
      { onSuccess: (data) => void navigate(`/clients/${data.client.id}`, { replace: true }) },
    );
  }

  /* The API validates the same schema this form does, so its per-field
     messages are the ones worth showing — they are already written for a
     human, in the same voice. */
  const fieldError = (name: keyof Fields): string | undefined =>
    create.error instanceof ApiError ? create.error.fields[name] : undefined;

  // A 422 names its fields; anything else is a whole-form problem.
  const formError =
    create.error instanceof ApiError && Object.keys(create.error.fields).length === 0
      ? create.error.message
      : null;

  if (leadId && lead.isPending) return <p className="muted">Loading…</p>;

  // Converting a lead that is already a client is a 409 waiting to happen.
  if (leadId && lead.data?.client) {
    return (
      <>
        <p className="alert" role="alert">
          This lead is already a client: {lead.data.client.business}.
        </p>
        <Link to={`/clients/${lead.data.client.id}`} className="btn">
          Open client
        </Link>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>New client</h1>
          <p className="muted">
            {leadId
              ? 'Filled in from the lead — check it, then save.'
              : 'For a client who did not come through a lead.'}
          </p>
        </div>
      </div>

      <form className="card stack" onSubmit={onSubmit} noValidate>
        <div className="field">
          <label htmlFor="business">Business name</label>
          <input
            id="business"
            value={fields.business}
            maxLength={160}
            onChange={(event) => set('business', event.target.value)}
          />
          {fieldError('business') ? (
            <span className="field__error" role="alert">
              {fieldError('business')}
            </span>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="ownerName">Owner name</label>
          <input
            id="ownerName"
            value={fields.ownerName}
            maxLength={120}
            onChange={(event) => set('ownerName', event.target.value)}
          />
          {fieldError('ownerName') ? (
            <span className="field__error" role="alert">
              {fieldError('ownerName')}
            </span>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="phone">WhatsApp number</label>
          <input
            id="phone"
            type="tel"
            inputMode="tel"
            value={fields.phone}
            onChange={(event) => set('phone', event.target.value)}
          />
          {fieldError('phone') ? (
            <span className="field__error" role="alert">
              {fieldError('phone')}
            </span>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            inputMode="email"
            value={fields.email}
            maxLength={200}
            onChange={(event) => set('email', event.target.value)}
          />
          <span className="muted" style={{ fontSize: 13 }}>
            Invoices go to this address.
          </span>
          {fieldError('email') ? (
            <span className="field__error" role="alert">
              {fieldError('email')}
            </span>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="gstin">GSTIN</label>
          <input
            id="gstin"
            value={fields.gstin}
            maxLength={15}
            autoCapitalize="characters"
            onChange={(event) => set('gstin', event.target.value)}
          />
          <span className="muted" style={{ fontSize: 13 }}>
            Leave it blank if there is none.
          </span>
          {fieldError('gstin') ? (
            <span className="field__error" role="alert">
              {fieldError('gstin')}
            </span>
          ) : null}
        </div>

        {formError ? (
          <p className="alert" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="row">
          <button className="btn" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Create client'}
          </button>
          <Link to={leadId ? `/leads/${leadId}` : '/clients'} className="btn btn--ghost">
            Cancel
          </Link>
        </div>

        {leadId ? (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Saving this marks the lead as won.
          </p>
        ) : null}
      </form>
    </>
  );
}
